import React, {useRef, useState} from "react";
import PropTypes from "prop-types";
import {AIcon, APopover, ATag, ATooltip} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import css from "./Chip.module.scss";

const Chip = ({
  children,
  circleIcon = false,
  className,
  small = true,
  leftIcon = "",
  popoverPlacement = "right",
  popoverContent,
  readOnly = false,
  rightIcon = "",
  tooltipClass,
  tooltipPlacement = "top",
  tooltipText,
  truncateText = false,
  ...rest
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);
  const chipRef = useRef(null);

  const chipClasses = classNames(css.chip, className, {
    [css.chipSmall]: small,
    [css.chipTooltip]: tooltipText || (popoverContent && !readOnly),
    [css.chipReadOnly]: readOnly
  });

  const iconClasses = classNames(css.icon, {
    [css.iconLeft]: leftIcon,
    [css.iconRight]: rightIcon,
    [css.iconLeftSmall]: small && leftIcon,
    [css.iconRightSmall]: small && rightIcon,
    [css.iconCircle]: circleIcon
  });

  const tooltipClasses = classNames("small-tooltip", {
    [tooltipClass]: tooltipClass
  });

  const renderIcon = (icon) => (
    <span className={iconClasses}>
      <AIcon>{icon}</AIcon>
    </span>
  );

  return (
    <ATag
      {...rest}
      aria-describedby={tooltipText}
      className={chipClasses}
      onMouseEnter={
        tooltipText || popoverContent ? () => setOpenTooltip(true) : undefined
      }
      onMouseLeave={
        tooltipText || popoverContent ? () => setOpenTooltip(false) : undefined
      }
      data-testid={rest?.["data-testid"] ? rest?.["data-testid"] : "Chip-Tag"}
      ref={chipRef}>
      {leftIcon && renderIcon(leftIcon)}
      <span
        className={classNames(css.chipText, {
          [css.chipTruncate]: truncateText
        })}>
        {children}
      </span>
      {rightIcon && renderIcon(rightIcon)}
      {tooltipText && (
        <ATooltip
          anchorRef={chipRef}
          className={tooltipClasses}
          open={openTooltip}
          placement={tooltipPlacement}
          data-testid={"Chip-Tooltip"}>
          {tooltipText}
        </ATooltip>
      )}
      {popoverContent && openTooltip && !readOnly && (
        <APopover
          className={css.popover}
          data-testid={"Chip-Popover"}
          anchorRef={chipRef}
          open={openTooltip}
          placement={popoverPlacement}>
          {popoverContent}
        </APopover>
      )}
    </ATag>
  );
};

export default Chip;

Chip.propTypes = {
  children: PropTypes.any,
  /**
   * If true, circle styling will be added around the icon
   *
   * @defaultValue false
   */
  circleIcon: PropTypes.bool,
  /**
   * Option to add a className to the parent component
   */
  className: PropTypes.string,
  /**
   * If true, a magna icon will display to the left of the text
   * Icons: https://magna-react.vercel.app/components/icon#magna-icon-gallery
   *
   * @defaultValue false
   */
  leftIcon: PropTypes.string,
  /**
   * If true, a magna icon will display to the right of the text
   * Icons: https://magna-react.vercel.app/components/icon#magna-icon-gallery
   *
   * @defaultValue false
   */
  rightIcon: PropTypes.string,
  /**
   * Content to be shown in popover
   */
  popoverContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Sets popover position
   *
   * @defaultValue "right"
   */
  popoverPlacement: PropTypes.oneOf([
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ]),
  /**
   * If true, small chip styling is applied
   *
   * @defaultValue true
   */
  small: PropTypes.bool,
  /**
   * Option to add a className to the tooltip
   */
  tooltipClass: PropTypes.string,
  /**
   * Sets tooltip position
   *
   * @defaultValue "top"
   */
  tooltipPlacement: PropTypes.oneOf([
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ]),
  /**
   * If tooltipText is provided, a tooltip description will be shown
   */
  tooltipText: PropTypes.string,
  /**
   * if truncateText is provided, text will truncate at width of 121px;
   *
   * @defaultValue false
   */
  truncateText: PropTypes.bool,
  /**
   * If true, hover/active/focus styling will be removed
   *
   * @defaultValue false
   */
  readOnly: PropTypes.bool
};
